import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client';
import { Field, LanguageSwitcher, Notice, PasswordInput } from '../components/ui';
import { useI18n, type TranslationKey } from '../i18n';
import { useSession } from '../auth/session-context';

/**
 * Anmeldung für Kundenadmins und Caller.
 *
 * Ein einziges Formular: wer eine E-Mail-Adresse eingibt, wird als
 * Kundenkonto angemeldet, wer einen Benutzernamen eingibt, als Caller.
 * Die beiden Abläufe sind serverseitig getrennt.
 */
export function LoginPage(): JSX.Element {
  const { t, tError } = useI18n();
  const { login, endReason } = useSession();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(identifier, password);
    } catch (caught) {
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="page page-narrow">
      <header className="page-header">
        <h1>{t('login.title')}</h1>
        <LanguageSwitcher />
      </header>

      {endReason && endReason !== 'logout' ? (
        <Notice tone="info">{t(`session.ended.${endReason}` as TranslationKey)}</Notice>
      ) : null}

      <form onSubmit={submit} className="form">
        <Field label={t('login.identifier')} hint={t('login.identifierHint')}>
          <input
            type="text"
            value={identifier}
            autoComplete="username"
            onChange={(event) => setIdentifier(event.target.value)}
          />
        </Field>

        <Field label={t('login.password')}>
          <PasswordInput value={password} onChange={setPassword} />
        </Field>

        {error ? <Notice tone="error">{error}</Notice> : null}

        <button type="submit" className="primary" disabled={busy}>
          {t('action.login')}
        </button>
      </form>

      <nav className="page-links">
        <Link to="/passwort-vergessen">{t('login.forgotPassword')}</Link>
        <Link to="/registrieren">{t('login.noAccount')}</Link>
      </nav>
    </main>
  );
}
